import React from "react";
import { Button } from "@/components/Button";
import { Heading, Text } from "@/components/Typography";
import { SuggestedAdvisor } from "@/services/api";
import { X } from "lucide-react";
import { getInitials } from "@/utils/util";

interface SendInvitationsModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedAdvisors: SuggestedAdvisor[];
  onSendInvites: () => void;
  isLoading?: boolean;
}

export const SendInvitationsModal: React.FC<SendInvitationsModalProps> = ({
  isOpen,
  onClose,
  selectedAdvisors,
  onSendInvites,
  isLoading = false,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="absolute inset-0 flex items-center justify-center p-8">
        <div className="bg-white dark:bg-surface-card rounded-lg shadow-xl transform transition-all max-w-[30rem] min-h-[30rem] w-full">
          <button
            onClick={onClose}
            className="p-4 hover:bg-gray-100 dark:hover:bg-surface-card-inner rounded-full transition-colors absolute top-0 right-0"
          >
            <X size={20} className="text-gray-500 dark:text-pab-dark-subtle-text" />
          </button>

          {/* Header */}
          <div className="flex flex-col p-4 border-b border-gray-200 dark:border-surface-border gap-2">
            <Heading as="h2" size="5" className="font-bold dark:text-white">
              Send Invitations to Your Experts
            </Heading>
            <Text className="text-gray-600 dark:text-pab-dark-subtle-text">
              We'll send each expert your profile and goals. You'll be notified as soon as they
              respond.
            </Text>
          </div>

          {/* Content */}
          <div className="p-2">
            {/* Selected Advisors List */}
            <div className="space-y-4 mb-4">
              {selectedAdvisors.map((advisor) => (
                <div
                  key={advisor.id}
                  className="flex items-center gap-2 p-2 bg-white dark:bg-surface-card-inner border border-gray-200 dark:border-surface-border shadow-md rounded-lg"
                >
                  <div className="w-16 h-16 rounded-full bg-white-500 dark:bg-surface-card flex items-center justify-center border border-primary-100 dark:border-pab-dark-avatar-border flex-shrink-0">
                    <span className="text-primary-500 dark:text-pab-dark-pill-text font-semibold text-sm">
                      {getInitials(advisor.name)}
                    </span>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-4 mb-1">
                      <Text className="font-bold truncate dark:text-white" size="3">
                        {advisor.name}
                      </Text>
                      {advisor.topMatch && (
                        <span className="px-4 py-1 bg-primary-100 text-primary-700 text-xs rounded-full font-medium">
                          TOP MATCH
                        </span>
                      )}
                    </div>
                    <Text
                      className="text-gray-600 dark:text-pab-dark-subtle-text truncate"
                      size="2"
                    >
                      {advisor.currentTitle}, {advisor.currentCompany}
                    </Text>
                  </div>
                </div>
              ))}
            </div>

            {/* Action Button */}
            <div className="flex justify-end">
              <Button
                variant="primary"
                onClick={onSendInvites}
                isLoading={isLoading}
                disabled={isLoading}
              >
                SEND INVITES
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
